import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { platformAdminAPI } from '../../services/api'

const EMPTY_FORM = {
  locode: '',
  port_type: 'sea',
  display_name: '',
  port_name: '',
  city: '',
  country_code: '',
  country_name: '',
  aliases: ''
}

export default function PortsAdmin() {
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({ total: 0, limit: 50, offset: 0 })
  const [filters, setFilters] = useState({ q: '', type: '', includeInactive: false })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async (overrides = {}) => {
    try {
      setLoading(true)
      setError(null)
      const params = {
        limit: meta.limit,
        offset: overrides.offset ?? meta.offset,
        ...(filters.q && { q: filters.q }),
        ...(filters.type && { type: filters.type }),
        ...(filters.includeInactive && { includeInactive: 'true' })
      }
      const res = await platformAdminAPI.listPorts(params)
      setRows(res.data?.data || [])
      setMeta(prev => ({ ...prev, ...(res.data?.meta || {}) }))
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load ports')
    } finally {
      setLoading(false)
    }
  }, [filters, meta.limit, meta.offset])

  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault()
    load({ offset: 0 })
  }

  const startCreate = () => {
    setEditing('new')
    setForm(EMPTY_FORM)
  }

  const startEdit = (row) => {
    setEditing(`${row.locode}/${row.port_type}`)
    setForm({
      locode: row.locode,
      port_type: row.port_type,
      display_name: row.display_name,
      port_name: row.port_name,
      city: row.city || '',
      country_code: row.country_code,
      country_name: row.country_name,
      aliases: (row.aliases || []).join(', ')
    })
  }

  const cancelEdit = () => {
    setEditing(null)
    setForm(EMPTY_FORM)
  }

  const submitForm = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const payload = {
        ...form,
        locode: form.locode.trim().toUpperCase(),
        country_code: form.country_code.trim().toUpperCase(),
        aliases: form.aliases.split(',').map(s => s.trim()).filter(Boolean)
      }
      if (editing === 'new') {
        await platformAdminAPI.createPort(payload)
      } else {
        const [locode, portType] = editing.split('/')
        const { locode: _, port_type: __, ...patch } = payload
        await platformAdminAPI.updatePort(locode, portType, patch)
      }
      cancelEdit()
      await load()
    } catch (err) {
      const apiErr = err.response?.data
      setError(apiErr?.details ? apiErr.details.join('; ') : (apiErr?.error || err.message || 'Save failed'))
    } finally {
      setSaving(false)
    }
  }

  const handleDeactivate = async (row) => {
    if (!window.confirm(`Deactivate ${row.locode} (${row.port_type})? It will stop appearing in autocomplete.`)) return
    try {
      await platformAdminAPI.deactivatePort(row.locode, row.port_type)
      await load()
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Deactivate failed')
    }
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Port Catalogue</h1>
        <Link to="/admin" className="btn btn-secondary">Back to Admin</Link>
      </div>

      <p style={{ marginBottom: '1rem', color: '#23201C', opacity: 0.7 }}>
        UN/LOCODE registry used by the chat extraction layer and the new shipment-creation form.
        Edit display names so codes resolve to the way your customers expect to read them.
      </p>

      {error && (
        <div style={{ padding: '0.75rem 1rem', marginBottom: '1rem', borderRadius: '6px', background: 'rgba(139,58,58,0.1)', border: '1px solid #8B3A3A', color: '#8B3A3A', fontSize: '0.875rem' }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSearch} style={{ display: 'flex', gap: '.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search code, name, city, country…"
          value={filters.q}
          onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
          style={{ flex: '1 1 240px', padding: '.5rem' }}
        />
        <select
          value={filters.type}
          onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
          style={{ padding: '.5rem' }}
        >
          <option value="">All types</option>
          <option value="sea">Sea</option>
          <option value="air">Air</option>
        </select>
        <label style={{ fontSize: '.875rem', display: 'flex', gap: '.25rem', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={filters.includeInactive}
            onChange={e => setFilters(f => ({ ...f, includeInactive: e.target.checked }))}
          />
          Show inactive
        </label>
        <button type="submit" className="btn btn-primary">Search</button>
        <button type="button" className="btn btn-secondary" onClick={startCreate}>+ Add port</button>
      </form>

      {editing && (
        <form onSubmit={submitForm} style={{ padding: '1rem', border: '1px solid #23201C', borderRadius: '8px', marginBottom: '1rem', background: '#F3F2ED' }}>
          <h3 style={{ marginTop: 0 }}>{editing === 'new' ? 'New port' : `Edit ${editing}`}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '.75rem' }}>
            <label>
              <div style={{ fontSize: '.75rem', opacity: .7 }}>LOCODE (5 chars)</div>
              <input
                type="text"
                maxLength="5"
                required
                value={form.locode}
                disabled={editing !== 'new'}
                onChange={e => setForm(f => ({ ...f, locode: e.target.value.toUpperCase() }))}
                style={{ width: '100%', padding: '.5rem' }}
              />
            </label>
            <label>
              <div style={{ fontSize: '.75rem', opacity: .7 }}>Type</div>
              <select
                value={form.port_type}
                disabled={editing !== 'new'}
                onChange={e => setForm(f => ({ ...f, port_type: e.target.value }))}
                style={{ width: '100%', padding: '.5rem' }}
              >
                <option value="sea">Sea</option>
                <option value="air">Air</option>
              </select>
            </label>
            <label>
              <div style={{ fontSize: '.75rem', opacity: .7 }}>Display name</div>
              <input
                type="text"
                required
                value={form.display_name}
                onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                style={{ width: '100%', padding: '.5rem' }}
              />
            </label>
            <label>
              <div style={{ fontSize: '.75rem', opacity: .7 }}>Port name</div>
              <input
                type="text"
                required
                value={form.port_name}
                onChange={e => setForm(f => ({ ...f, port_name: e.target.value }))}
                style={{ width: '100%', padding: '.5rem' }}
              />
            </label>
            <label>
              <div style={{ fontSize: '.75rem', opacity: .7 }}>City</div>
              <input
                type="text"
                value={form.city}
                onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                style={{ width: '100%', padding: '.5rem' }}
              />
            </label>
            <label>
              <div style={{ fontSize: '.75rem', opacity: .7 }}>Country code (ISO-2)</div>
              <input
                type="text"
                maxLength="2"
                required
                value={form.country_code}
                onChange={e => setForm(f => ({ ...f, country_code: e.target.value.toUpperCase() }))}
                style={{ width: '100%', padding: '.5rem' }}
              />
            </label>
            <label>
              <div style={{ fontSize: '.75rem', opacity: .7 }}>Country name</div>
              <input
                type="text"
                required
                value={form.country_name}
                onChange={e => setForm(f => ({ ...f, country_name: e.target.value }))}
                style={{ width: '100%', padding: '.5rem' }}
              />
            </label>
            <label style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: '.75rem', opacity: .7 }}>Aliases (comma separated)</div>
              <input
                type="text"
                value={form.aliases}
                onChange={e => setForm(f => ({ ...f, aliases: e.target.value }))}
                placeholder="e.g. Bombay, Mumbai Port Trust"
                style={{ width: '100%', padding: '.5rem' }}
              />
            </label>
          </div>
          <div style={{ marginTop: '.75rem', display: 'flex', gap: '.5rem' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : (editing === 'new' ? 'Create' : 'Save')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={cancelEdit} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="loading">Loading ports…</div>
      ) : (
        <>
          <div style={{ marginBottom: '.5rem', fontSize: '.875rem', opacity: .7 }}>
            Showing {rows.length} of {meta.total} ports
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.875rem' }}>
            <thead>
              <tr style={{ background: '#23201C', color: '#FFFFFF' }}>
                <th style={{ padding: '.5rem', textAlign: 'left' }}>LOCODE</th>
                <th style={{ padding: '.5rem', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '.5rem', textAlign: 'left' }}>Display name</th>
                <th style={{ padding: '.5rem', textAlign: 'left' }}>City</th>
                <th style={{ padding: '.5rem', textAlign: 'left' }}>Country</th>
                <th style={{ padding: '.5rem', textAlign: 'left' }}>Active</th>
                <th style={{ padding: '.5rem', textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan="7" style={{ padding: '1rem', textAlign: 'center', opacity: .7 }}>No ports match.</td></tr>
              )}
              {rows.map((row, idx) => (
                <tr key={`${row.locode}-${row.port_type}`} style={{ background: idx % 2 ? '#F3F2ED' : '#FFFFFF', opacity: row.is_active ? 1 : 0.5 }}>
                  <td style={{ padding: '.5rem', fontFamily: 'monospace' }}>{row.locode}</td>
                  <td style={{ padding: '.5rem' }}>{row.port_type}</td>
                  <td style={{ padding: '.5rem' }}>{row.display_name}</td>
                  <td style={{ padding: '.5rem' }}>{row.city || '—'}</td>
                  <td style={{ padding: '.5rem' }}>{row.country_name} ({row.country_code})</td>
                  <td style={{ padding: '.5rem' }}>{row.is_active ? 'yes' : 'no'}</td>
                  <td style={{ padding: '.5rem', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-sm btn-secondary" onClick={() => startEdit(row)}>Edit</button>
                    {row.is_active && (
                      <button
                        className="btn btn-sm btn-secondary"
                        style={{ marginLeft: '.25rem' }}
                        onClick={() => handleDeactivate(row)}
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}
